/**
 * Title Strategy - Optimized Version
 * 
 * This strategy handles the title topic by generating summary messages instead of
 * serializing entire components. It only caches excluded titles and shows a snapshot
 * of titles in the UI component.
 */

import { runAIMatches, runMatchesAnalyze, fetchQueries } from '../api';
import { TitleOptions } from './title/components';
import generateEnhancedGuidance from './enhancedGuidance';
import { createBaseStrategy, StrategyPhase } from './baseStrategy';
import { buildSummaryMacroInfoV2 } from '../utils';

// Track if the user has rejected title suggestions
let userRejectedTitleSuggestions = false;

// Cache for excluded titles (only store what's excluded, not all titles)
let excludedTitlesCache = [];

/**
 * Generate title summary message with included/excluded titles
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @param {Object} decision - Optional decision object that may contain handleRefreshResults and queries
 * @returns {Object} Object containing summary message and title data
 */
const generateTitleSummaryMessage = async (messages, setMessages, decision = null) => {
  console.log('🏷️ generateTitleSummaryMessage: Starting title data generation...', {
    messagesLength: messages?.length || 0,
    decision: decision ? 'present' : 'null',
    hasQueries: !!(decision?.queries),
    queriesCount: decision?.queries ? Object.keys(decision.queries).length : 0
  });
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating title analysis...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Check if messages are empty but we have ICP requirements in the central queue
  const hasEmptyMessages = !messages || messages.length === 0;
  let regularMatches = null;
  let requirements = [];
  
  if (hasEmptyMessages) {
    // Try to get ICP requirements from central discovery queue
    try {
      const centralDiscoveryQueueModule = await import('../centralDiscoveryQueue');
      const centralDiscoveryQueue = centralDiscoveryQueueModule.default;
      const icpRequirements = centralDiscoveryQueue.getCurrentICPRequirements();
      
      if (icpRequirements && icpRequirements.length > 0) {
        console.log('🏷️ generateTitleSummaryMessage: Found existing ICP requirements, building summary...', icpRequirements.length);
        
        // Fetch queries/macros data
        const queries = await fetchQueries();
        
        if (queries) {
          // Build summary using existing requirements
          const summaryText = buildSummaryMacroInfoV2(icpRequirements, queries);
          
          // Create a synthetic messages array with the summary
          const syntheticMessages = [
            {
              role: 'human',
              content: `Based on the existing requirements: ${summaryText}. Now let's work on job titles for this role.`
            }
          ];
          
          console.log('🏷️ generateTitleSummaryMessage: Using synthetic messages with summary');
          regularMatches = await runAIMatches(syntheticMessages);
          requirements = icpRequirements;
        }
      }
    } catch (error) {
      console.error('🏷️ generateTitleSummaryMessage: Error getting ICP requirements from queue:', error);
    }
  }
  
  // If we don't have regularMatches yet, use the original approach
  if (!regularMatches) {
    console.log('🏷️ generateTitleSummaryMessage: Calling runAIMatches...');
    regularMatches = await runAIMatches(messages || []);
  }
  // Update match results in the sidebar if handleRefreshResults is provided
  if (setMessages && decision && decision.handleRefreshResults) {
    console.log('Updating match results in sidebar with handleRefreshResults');
    decision.handleRefreshResults(messages, regularMatches);
  }
  
  if (!regularMatches) {
    throw new Error('Failed to get AI matches');
  }
  
  // Extract requirements from regularMatches (if we don't already have them from queue)
  if (requirements.length === 0) {
    requirements =
      regularMatches.search_gen_response &&
      regularMatches.search_gen_response.icp_requirements
        ? regularMatches.search_gen_response.icp_requirements
        : [];
  }
  
  // Run matchesAnalyze to get title distribution data
  console.log('🏷️ generateTitleSummaryMessage: Calling runMatchesAnalyze with requirements:', requirements.length);
  const matchesAnalyzeResult = await runMatchesAnalyze(requirements, {
    run_on_self_org: false
  });
  
  console.log('🏷️ generateTitleSummaryMessage: runMatchesAnalyze result:', {
    hasResult: !!matchesAnalyzeResult,
    hasAnalysisInfo: !!(matchesAnalyzeResult?.analysis_info),
    hasTitleMap: !!(matchesAnalyzeResult?.analysis_info?.title_map),
    titleMapKeys: matchesAnalyzeResult?.analysis_info?.title_map ? Object.keys(matchesAnalyzeResult.analysis_info.title_map).length : 0
  });
  
  // Process title data if available
  if (matchesAnalyzeResult && matchesAnalyzeResult.analysis_info && matchesAnalyzeResult.analysis_info.title_map) {
    const titleMap = matchesAnalyzeResult.analysis_info.title_map;
    
    // Sort title keys by the number of profiles (array length) in descending order
    const allTitleKeys = Object.keys(titleMap)
      .sort((a, b) => {
        const aProfiles = Array.isArray(titleMap[a]) ? titleMap[a].length : 0;
        const bProfiles = Array.isArray(titleMap[b]) ? titleMap[b].length : 0;
        return bProfiles - aProfiles;
      });
    
    // Take only top 5 titles for UI display (snapshot)
    const topTitles = allTitleKeys.slice(0, 10);
    
    console.log('Title strategy: Found', allTitleKeys.length, 'total titles, showing top 10:', topTitles);
    
    // Generate summary message based on current excluded titles
    let summaryMessage = '';
    if (excludedTitlesCache.length === 0) {
      // No exclusions yet
      const titleList = topTitles.slice(0, 3).map(title => `"${title}"`).join(', ');
      summaryMessage = `Based on your requirements, I found relevant job titles including ${titleList} and others. Would you like to review and refine these title options?`;
    } else {
      // Some titles are excluded
      const includedTitles = topTitles.filter(title => !excludedTitlesCache.includes(title));
      const excludedInTop = topTitles.filter(title => excludedTitlesCache.includes(title));
      
      if (includedTitles.length > 0 && excludedInTop.length > 0) {
        const includeList = includedTitles.slice(0, 2).map(title => `"${title}"`).join(', ');
        const excludeList = excludedInTop.map(title => `"${title}"`).join(', ');
        summaryMessage = `Perfect! I'll include titles like ${includeList} and exclude ${excludeList} from the search.`;
      } else if (includedTitles.length > 0) {
        const includeList = includedTitles.slice(0, 3).map(title => `"${title}"`).join(', ');
        summaryMessage = `Great! I'll focus on titles like ${includeList} for the search.`;
      } else {
        summaryMessage = `I've noted your title preferences and will adjust the search accordingly.`;
      }
    }
    
    return {
      aiMatchesResult: regularMatches,
      matchesAnalyzeResult,
      summaryMessage,
      topTitles, // Only top 5 for UI
      allTitleKeys, // All titles for reference (not serialized)
      titleMap
    };
  }
  
  return {
    aiMatchesResult: regularMatches,
    matchesAnalyzeResult: null,
    summaryMessage: 'Let me help you refine the job title for this role.',
    topTitles: [],
    allTitleKeys: [],
    titleMap: {}
  };
};

/**
 * Handler for the init phase of the title strategy
 */
const initPhaseHandler = async (decision, messages, setMessages) => {
  console.log('🏷️ TITLE STRATEGY: init phase - CALLED SUCCESSFULLY', {
    decision: decision ? 'present' : 'null',
    messages: messages ? messages.length : 'null',
    setMessages: setMessages ? 'present' : 'null',
    hasQueries: !!(decision?.queries),
    queriesCount: decision?.queries ? Object.keys(decision.queries).length : 0,
    queriesIsNull: decision?.queries === null,
    queriesIsUndefined: decision?.queries === undefined,
    queriesIsEmpty: decision?.queries && Object.keys(decision.queries).length === 0
  });

  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nInitializing title discovery...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Check if the user has rejected title suggestions
  if (userRejectedTitleSuggestions || 
      (decision.actionType === 'USER_REJECTED_TITLE_SUGGESTIONS')) {
    
    console.log('User rejected title suggestions, sending to backend');
    userRejectedTitleSuggestions = true;
    
    const titleGuidance = `
IMPORTANT: The current topic is JOB TITLE. The user has rejected our suggested titles.

Focus on discovering what job titles would be more appropriate for this role:
1. Ask the user what kinds of job titles they are looking for
2. Explore different title options based on their feedback
3. Consider seniority level, specialization, and industry standards
4. Suggest alternative titles that might better reflect the role's responsibilities

After gathering sufficient information, summarize the title requirements clearly and ask if they're ready to move on to the next topic.
`;
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: titleGuidance,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
  
  try {
    // 🔧 FIX: Create queue items for title discovery instead of just returning components
    // This ensures the Simple interface can show title options in the main content area
    
    const centralDiscoveryQueueModule = await import('../centralDiscoveryQueue');
    const centralDiscoveryQueue = centralDiscoveryQueueModule.default;
    
    // Generate title summary data
    console.log('🏷️ TITLE STRATEGY: Calling generateTitleSummaryMessage...');
    const titleData = await generateTitleSummaryMessage(messages, setMessages, decision);
    
    console.log('🏷️ TITLE STRATEGY: generateTitleSummaryMessage result:', {
      topTitlesCount: titleData?.topTitles?.length || 0,
      topTitles: titleData?.topTitles || [],
      hasAiMatchesResult: !!titleData?.aiMatchesResult
    });
    
    // Check if we have title data
    if (titleData.topTitles.length > 0) {
      console.log('🏷️ TITLE STRATEGY: Creating queue item with', titleData.topTitles.length, 'title options');
      
      // Create a queue item for title selection
      const queueId = `role_${Date.now()}`;
      const titleQueueItem = {
        queue_id: queueId,
        section_id: 'role',
        section_name: 'Job Title',
        section_type: 'Must Have',
        topic: 'title',
        disco_done: false,
        skipped: false,
        bool_str: '',
        
        // Template configuration for MultiRowSelectorTemplate
        template_type: 'MultiRowSelectorTemplate',
        ai_intro_message: "Based on your requirements, I found relevant job titles. Pick the titles you'd like to focus on.",
        ai_confirmation_message: "Great! I've noted your job title preferences.",
        
        ux_config: {
          title: "Job Title Selection",
          confirm_button_text: "Confirm Titles",
          allow_empty_selection: false,
          show_skip_button: true
        },
        
        template_props: {
          title: "Based on your requirements, here are relevant job titles you might want to target:",
          sections: [{
            items: titleData.topTitles.map(title => ({
              value: `workexp.current.title:${title}`,
              label: title
            }))
          }],
          selectedItems: [], // Start with no pre-selection - user must choose
          confirmButtonText: "Confirm Titles",
          allowEmptySelection: false,
          showSkipButton: true,
          skipButtonText: "Skip Title Selection",
          // Agentic flow props
          enableAgenticToggle: true,
          topic: 'job_titles',
          agenticContext: `Job title selection context. Please select the most relevant job titles from the available options that best match the hiring requirements for this role.`,
          sessionId: decision?.sessionId || 'default-session'
        },
        
        created_at: Date.now(),
        updated_at: Date.now()
      };
      
      // Add the queue item to central discovery queue
      await centralDiscoveryQueue.addQueueItem(titleQueueItem);
      
      console.log('✅ Title strategy: Added title queue item to central discovery queue:', queueId);
      
      // Return success - queue item has been created
      return {
        ...decision,
        actionType: 'QUEUE_ITEMS_CREATED',
        queueItemsCreated: 1,
        strategyPhase: StrategyPhase.SUGGESTIONS
      };
      
    } else {
      console.log('🏷️ TITLE STRATEGY: No title data available, creating fallback queue item');
      console.log('🏷️ TITLE STRATEGY: About to create manual title queue item...');
      
      // Create a fallback queue item for manual title input
      const queueId = `title_manual_${Date.now()}`;
      const manualTitleQueueItem = {
        queue_id: queueId,
        section_id: 'title_manual',
        section_name: 'Job Title',
        section_type: 'Must Have',
        topic: 'title',
        disco_done: false,
        skipped: false,
        bool_str: '',
        
        // Use conversation template for manual input
        template_type: 'ConversationTemplate',
        ai_intro_message: "Let me help you define the job title for this role. What kind of job title are you looking for?",
        ai_confirmation_message: "Perfect! I've captured your job title requirements.",
        
        ux_config: {
          title: "Job Title Definition",
          conversation_mode: true
        },
        
        template_props: {
          conversation_prompt: "Please describe the job title you're looking for (e.g., 'Senior Software Engineer', 'Marketing Manager', etc.)",
          placeholder: "Enter job title or description..."
        },
        
        created_at: Date.now(),
        updated_at: Date.now()
      };
      
      // Add the queue item to central discovery queue
      await centralDiscoveryQueue.addQueueItem(manualTitleQueueItem);
      
      console.log('✅ Title strategy: Added manual title queue item to central discovery queue:', queueId);
      
      // Return success - fallback queue item has been created
      return {
        ...decision,
        actionType: 'QUEUE_ITEMS_CREATED',
        queueItemsCreated: 1,
        strategyPhase: StrategyPhase.SUGGESTIONS
      };
    }
  } catch (error) {
    console.error('🚨 Title strategy: Error generating title data:', error);
    
    try {
      // Create a fallback queue item even if title data generation fails
      const centralDiscoveryQueueModule = await import('../centralDiscoveryQueue');
    const centralDiscoveryQueue = centralDiscoveryQueueModule.default;
      
      const queueId = `title_fallback_${Date.now()}`;
      const fallbackTitleQueueItem = {
        queue_id: queueId,
        section_id: 'title_fallback',
        section_name: 'Job Title',
        section_type: 'Must Have',
        topic: 'title',
        disco_done: false,
        skipped: false,
        bool_str: '',
        
        // Use conversation template as fallback
        template_type: 'ConversationTemplate',
        ai_intro_message: "Let's work on defining the job title for this role. What kind of position are you looking to fill?",
        ai_confirmation_message: "Got it! I've captured your job title requirements.",
        
        ux_config: {
          title: "Job Title Requirements",
          conversation_mode: true
        },
        
        template_props: {
          conversation_prompt: "Please describe the job title and role you're hiring for",
          placeholder: "e.g., Senior Software Engineer, Marketing Director..."
        },
        
        created_at: Date.now(),
        updated_at: Date.now()
      };
      
      await centralDiscoveryQueue.addQueueItem(fallbackTitleQueueItem);
      
      console.log('✅ Title strategy: Created fallback queue item after error:', queueId);
      
      return {
        ...decision,
        actionType: 'QUEUE_ITEMS_CREATED',
        queueItemsCreated: 1,
        strategyPhase: StrategyPhase.SUGGESTIONS
      };
      
    } catch (fallbackError) {
      console.error('🚨 Title strategy: Failed to create fallback queue item:', fallbackError);
      
      // Last resort - return backend guidance
      const defaultGuidance = `Focus on refining the job title for this role. Ask about the level of seniority, the team structure, and how this role fits into the organization.`;
      
      return {
        ...decision,
        actionType: 'SEND_TO_BACKEND',
        agentGuidance: defaultGuidance,
        strategyPhase: StrategyPhase.SUGGESTIONS
      };
    }
  }
};

/**
 * Handler for the suggestions phase of the title strategy
 */
const suggestionsPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Title strategy: suggestions phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating title suggestions...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Check if the user has rejected title suggestions
  if (userRejectedTitleSuggestions || 
      (decision.actionType === 'USER_REJECTED_TITLE_SUGGESTIONS')) {
    
    console.log('User rejected title suggestions, sending to backend');
    userRejectedTitleSuggestions = true;
    
    const titleGuidance = `
IMPORTANT: The current topic is JOB TITLE. The user has rejected our suggested titles.

Focus on discovering what job titles would be more appropriate for this role:
1. Ask the user what kinds of job titles they are looking for
2. Explore different title options based on their feedback
3. Consider seniority level, specialization, and industry standards
4. Suggest alternative titles that might better reflect the role's responsibilities

After gathering sufficient information, summarize the title requirements clearly and ask if they're ready to move on to the next topic.
`;
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: titleGuidance,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
  
  try {
    // Generate title summary data
    const titleData = await generateTitleSummaryMessage(messages, setMessages, decision);
    
    // Check if we have title data
    if (titleData.topTitles.length > 0) {
      console.log('Title strategy (suggestions): Showing title options with', titleData.topTitles.length, 'titles');
      
      // Return a decision that will render the title options component (lightweight)
      return {
        ...decision,
        actionType: 'RENDER_TITLE_OPTIONS',
        component: TitleOptions,
        aiMatchesResult: titleData.aiMatchesResult,
        strategyPhase: StrategyPhase.CONVERSATION,
        componentProps: {
          titleKeys: titleData.topTitles, // Only top 5 titles
          advanceToNextTopic: decision.advanceToNextTopic,
          setMessages,
          onSendText: decision.onSendText,
          messages,
          onTitlesSelected: (selectedTitles, confirmationData) => {
            // Cache only the excluded titles
            const excludedTitles = titleData.topTitles.filter(title => !selectedTitles.includes(title));
            excludedTitlesCache = [...excludedTitles];
            
            console.log('Titles selected:', selectedTitles.length, 'Excluded:', excludedTitles.length);
            
            // Send summary message to backend instead of serializing component
            return {
              ...decision,
              actionType: 'SEND_TITLE_SUMMARY',
              summaryMessage: confirmationData.message,
              selectedTitles,
              excludedTitles,
              confirmationData
            };
          }
        }
      };
    } else {
      // Generate enhanced agent guidance
      const enhancedGuidance = await generateEnhancedGuidance(
        titleData.aiMatchesResult, 
        decision, 
        'title'
      );
      
      return {
        ...decision,
        actionType: 'SEND_WITH_GUIDANCE',
        agentGuidance: enhancedGuidance,
        aiMatchesResult: titleData.aiMatchesResult,
        strategyPhase: StrategyPhase.CONVERSATION
      };
    }
  } catch (error) {
    console.error('Error generating title suggestions:', error);
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for the conversation phase of the title strategy
 */
const conversationPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Title strategy: conversation phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nContinuing conversation about title...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // Check if this is a user rejection action
  if (decision.actionType === 'USER_REJECTED_TITLE_SUGGESTIONS') {
    console.log('User rejected title suggestions, setting flag to true');
    userRejectedTitleSuggestions = true;
  }
  
  // Handle title summary action (when user confirms title selection)
  if (decision.actionType === 'SEND_TITLE_SUMMARY') {
    console.log('Sending title summary to backend');
    
    // Create guidance that includes the summary message and excluded titles info
    const titleSummaryGuidance = `
IMPORTANT: The user has just confirmed their title preferences.

TITLE SUMMARY: ${decision.summaryMessage}

EXCLUDED TITLES: ${decision.excludedTitles.length > 0 ? decision.excludedTitles.join(', ') : 'None'}
SELECTED TITLES: ${decision.selectedTitles.join(', ')}

Acknowledge their title selection and ask if they're ready to move on to the next topic (company and industry context).

Example response: "Perfect! I've noted your title preferences. ${decision.summaryMessage} Shall we move on to discussing the company and industry context for this role?"
`;
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: titleSummaryGuidance,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
  
  try {
    // Generate title summary data
    const titleData = await generateTitleSummaryMessage(messages, setMessages, decision);
    
    // Check if we have title data and should show options
    if (titleData.topTitles.length > 0 && !userRejectedTitleSuggestions) {
      console.log('Title strategy (conversation): Showing title options with', titleData.topTitles.length, 'titles');
      
      // Return a decision that will render the title options component (lightweight)
      return {
        ...decision,
        actionType: 'RENDER_TITLE_OPTIONS',
        component: TitleOptions,
        aiMatchesResult: titleData.aiMatchesResult,
        strategyPhase: StrategyPhase.CONVERSATION,
        componentProps: {
          titleKeys: titleData.topTitles, // Only top 5 titles
          advanceToNextTopic: decision.advanceToNextTopic,
          setMessages,
          onSendText: decision.onSendText,
          messages,
          onTitlesSelected: (selectedTitles, confirmationData) => {
            // Cache only the excluded titles
            const excludedTitles = titleData.topTitles.filter(title => !selectedTitles.includes(title));
            excludedTitlesCache = [...excludedTitles];
            
            console.log('Titles selected:', selectedTitles.length, 'Excluded:', excludedTitles.length);
            
            // Send summary message to backend instead of serializing component
            return {
              ...decision,
              actionType: 'SEND_TITLE_SUMMARY',
              summaryMessage: confirmationData.message,
              selectedTitles,
              excludedTitles,
              confirmationData
            };
          }
        }
      };
    } else {
      // Generate specific guidance for the title conversation phase
      const titleConversationGuidance = `
IMPORTANT: The current topic is JOB TITLE. Focus ONLY on discussing the job title for this role.

1. First, acknowledge any title information the user has provided and ask for more details:
   - What level of seniority does this title represent?
   - How does this role fit into the team structure?
   - Are there any alternative titles that might better reflect the responsibilities?

2. Then, explore other aspects of the title that might be relevant:
   - Is this a new role or an existing one?
   - How does this title compare to similar roles in the industry?
   - Are there any specific qualifications or certifications that should be reflected in the title?

3. After gathering sufficient information, summarize the title requirements clearly:
   - "Based on our conversation, it sounds like the most appropriate title would be [summarize title]..."

4. Finally, EXPLICITLY ask if they're ready to move on to the next topic:
   - "Does that title sound accurate for the role? If so, shall we move on to discussing company and industry context?"
   - "Have I captured the job title correctly? If yes, we can proceed to the next area."

DO NOT change the topic to discussing skills, experience, or any other topic until the user has EXPLICITLY confirmed they're ready to move on from the title discussion.
`;
  
      return {
        ...decision,
        actionType: 'SEND_TO_BACKEND',
        agentGuidance: titleConversationGuidance,
        strategyPhase: StrategyPhase.CONVERSATION
      };
    }
  } catch (error) {
    console.error('Error in title conversation phase:', error);
    
    const titleConversationGuidance = `
IMPORTANT: The current topic is JOB TITLE. Focus ONLY on discussing the job title for this role.

1. First, acknowledge any title information the user has provided and ask for more details:
   - What level of seniority does this title represent?
   - How does this role fit into the team structure?
   - Are there any alternative titles that might better reflect the responsibilities?

2. Then, explore other aspects of the title that might be relevant:
   - Is this a new role or an existing one?
   - How does this title compare to similar roles in the industry?
   - Are there any specific qualifications or certifications that should be reflected in the title?

3. After gathering sufficient information, summarize the title requirements clearly:
   - "Based on our conversation, it sounds like the most appropriate title would be [summarize title]..."

4. Finally, EXPLICITLY ask if they're ready to move on to the next topic:
   - "Does that title sound accurate for the role? If so, shall we move on to discussing company and industry context?"
   - "Have I captured the job title correctly? If yes, we can proceed to the next area."

DO NOT change the topic to discussing skills, experience, or any other topic until the user has EXPLICITLY confirmed they're ready to move on from the title discussion.
`;
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: titleConversationGuidance,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Title strategy using the phase-based approach
 */
const titleStrategy = createBaseStrategy({
  topicName: 'title',
  initPhaseHandler,
  suggestionsPhaseHandler,
  conversationPhaseHandler
});

export default titleStrategy;
